import { useState, useEffect } from 'react';
import { ChatCard } from './ChatCard';
import chatService from '../../services/chat.service';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import DotSpinner from '../../components/shared/DotSpinner';
import { calculateAge } from '../../utils/calculateAge';
import { useSoftDeletedChat } from './hooks/useSoftDeletedChat';
import { useChat } from '../../context/ChatProvider';
import { useRef } from 'react';
import config from '../../config';
import { useObserver } from '../../hooks/useObserver';

const Chats = () => {
  const axiosPrivate = useAxiosPrivate();

  const { chats, uniqueSetChats, deleteChatById } = useChat();
  const [loading, setLoading] = useState(true);

  const [hiddenChats, setHiddenChats] = useState(new Set());
  const { onDeleteChat, ToastDeleting } = useSoftDeletedChat({
    onCallBackDeleted: (chatId) => {
      deleteChatById(chatId);
      setHiddenChats((prev) => {
        const newSet = new Set(prev);
        newSet.delete(chatId);
        return newSet;
      });
    },
    onHide: (chatId) => {
      setHiddenChats((prev) => new Set(prev).add(chatId));
    },
    onRestore: (chatId) => {
      setHiddenChats((prev) => {
        const newSet = new Set(prev);
        newSet.delete(chatId);
        return newSet;
      });
    },
  });

  const [searchName, setSearchName] = useState('');
  const [searchChats, setSearchChats] = useState([]);
  const вebounceSearchRef = useRef(null);

  const [totalItems, setTotalItems] = useState(null);
  const [page, setPage] = useState(1);
  const isLoadingChatsRef = useRef(false);
  const targetRef = useObserver({
    onIntersecting: () => {
      if (chats.length >= totalItems || chats.length === 0) return;
      fetchChats(page + 1);
    },
    rootMargin: '100px 0px 0px 0px',
  });

  useEffect(() => {
    fetchChats(page);
  }, []);

  useEffect(() => {
    if (вebounceSearchRef.current) clearTimeout(вebounceSearchRef.current);
    if (!searchName.length) return;

    вebounceSearchRef.current = setTimeout(() => {
      fetchFindChatUserByUserName();
    }, 1500);
  }, [searchName]);

  const fetchChats = async (page) => {
    if (isLoadingChatsRef.current) return;
    isLoadingChatsRef.current = true;

    try {
      const response = await chatService.getUserChats(axiosPrivate, page);

      if (!response || !response.data || !response.data.chats) {
        throw new Error('Invalid response format from server');
      }

      uniqueSetChats(
        response.data.chats.items.filter((i) => !i.blockedByMe),
        page === 1,
      );
      setTotalItems(response.data.chats.total);
      setPage(page);
    } catch (err) {
      console.error('Error fetching chats:', err);
    } finally {
      setLoading(false);
      isLoadingChatsRef.current = false;
    }
  };

  const fetchFindChatUserByUserName = async () => {
    try {
      setLoading(true);
      const response = await chatService.getUserChats(axiosPrivate, 1, 20, searchName);
      setSearchChats(response.data.chats.items);
    } catch (error) {
      console.error('[Chats] error to fetchFindChatUserByUserName', error);
    } finally {
      setLoading(false);
    }
  };

  const onChatDeleted = async (chatId) => {
    onDeleteChat(chatId, fetchChats);
  };

  return (
    <div className="relative w-full pt-[21px] pb-[80px] flex flex-col items-center overflow-hidden">
      <div className="relative w-full flex justify-center h-[64px] px-4">
        <div className="w-full relative">
          <input
            value={searchName}
            onChange={(e) => setSearchName(e.target.value.trim())}
            type="text"
            placeholder="Поиск"
            className="w-full h-[64px] rounded-[8px] bg-[#022424] border-[1px] border-[#233636] pl-12 text-white"
          />
          <img
            alt="search"
            src="/icons/search.svg"
            className="absolute top-1/2 -translate-y-1/2 left-[15px] w-[20px] h-[20px]"
          />
        </div>
      </div>

      <div className="mt-[20px] overflow-scroll">
        {loading && chats.length > 0 && (
          <div className="flex flex-col items-center justify-center w-full mt-[200px] text-white">
            <DotSpinner />
          </div>
        )}

        {chats.length > 0 &&
          !searchName.length &&
          chats.filter((chat) => !hiddenChats.has(chat._id)).map((chat) => {
            return (
              <div key={chat._id} className="border-b border-[#233636] w-[100vw] flex justify-center z-5">
                <ChatCard
                  id={chat._id}
                  userId={chat.partner._id}
                  img={chat.partner.photo || '/mock/stream_chat_user_avatar.png'}
                  name={chat.partner.name || 'Unknown User'}
                  age={calculateAge(chat.partner.birthDay)}
                  lMsg={chat.blockedMe ? 'Вы заблокированы пользователем' : chat.lastMessage || null}
                  time={
                    chat.lastMessage?.createdAt
                      ? new Date(chat.lastMessage.createdAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : ''
                  }
                  count={chat.unread || 0}
                  link_to_full_chat={config.ROUTES.CHATS.TEXT(chat._id)}
                  onDelete={onChatDeleted}
                />
              </div>
            );
          })}

        {searchName.length === 0 && <div ref={targetRef} className="h-2" />}

        {searchName.length > 0 &&
          searchChats.length > 0 &&
          searchChats.filter((chat) => !hiddenChats.has(chat._id)).map((chat) => {
            return (
              <div key={chat._id} className="border-b border-[#233636] w-[100vw] flex justify-center z-5">
                <ChatCard
                  id={chat._id}
                  userId={chat.partner._id}
                  img={chat.partner.photo || '/mock/stream_chat_user_avatar.png'}
                  name={chat.partner.name || 'Unknown User'}
                  age={calculateAge(chat.partner.birthDay)}
                  lMsg={chat.lastMessage?.text || ''}
                  time={
                    chat.lastMessage?.createdAt
                      ? new Date(chat.lastMessage.createdAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : ''
                  }
                  count={chat.unread || 0}
                  link_to_full_chat={`/chats/${chat._id}`}
                  onDelete={onChatDeleted}
                />
              </div>
            );
          })}

        {!loading && searchName.length > 0 && !searchChats.length && (
          <p className="text-lg text-white">Совпадений не найдено...</p>
        )}

        {!loading && !chats.length && (
          <div className="flex flex-col items-center justify-center w-full mt-[200px] text-white">
            <img alt="empty chats icon" src="/icons/empty_chats.svg" className="w-[48px] h-[48px]" />
            <p className="text-[24px] mt-[30px]">У вас пока нет чатов</p>
            <p className="text-[16px] mt-[10px]">Здесь появится ваши переписки</p>
          </div>
        )}
      </div>

      <ToastDeleting />
    </div>
  );
};

export default Chats;
